/**
 * Conflict Queue — Phase 2
 *
 * Shows all conflicted and quarantined change_events and allows
 * workspace admins to review and resolve each one.
 *
 * Resolution options per conflict:
 *   Keep local   — priorities.ai value wins; dispatch it back to the
 *                  external system via the /dispatch edge function.
 *   Accept external — external value wins; caller must apply it to the
 *                  local entity (currently manual; Phase 3 automates this).
 *   Dismiss      — mark as skipped without applying either value.
 *
 * Route: /prioritization/settings/conflict-queue
 */

import { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  ArrowDownToLine,
  ArrowLeftRight,
  Bot,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Loader2,
  RefreshCcw,
  ShieldAlert,
  XCircle,
  Zap,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { getSafeSupabaseClient, getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import {
  resolveConflict,
  sweepQuarantines,
  triggerDispatch,
  type ChangeEvent,
  type ConflictData,
  type SyncStatus,
  CONFLICT_QUARANTINE_SLA_MS,
} from '../../../lib/syncEngine';
import { triggerApply } from '../../../lib/connectors';
import {
  suggestConflictResolution,
  type ConflictAdvisorResult,
} from '../../../lib/llmSyncAdvisorService';
import { useAiCapability } from '../../../lib/ai/useAiCapability';

// ── Helpers ───────────────────────────────────────────────────

const SOURCE_LABEL: Record<string, string> = {
  priorities_ai: 'priorities.ai',
  jira: 'Jira',
  linear: 'Linear',
  github: 'GitHub',
  miro: 'Miro',
  slack: 'Slack',
  csv: 'CSV Import/Export',
};

function formatTimestamp(iso: string) {
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    }).format(new Date(iso));
  } catch { return iso; }
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v.length > 80 ? v.slice(0, 80) + '…' : v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v).slice(0, 100);
}

function timeUntilQuarantine(createdAt: string): string {
  const remainingMs =
    CONFLICT_QUARANTINE_SLA_MS - (Date.now() - new Date(createdAt).getTime());
  if (remainingMs <= 0) return 'Past SLA — quarantine pending';
  const hours = Math.floor(remainingMs / 3_600_000);
  const minutes = Math.floor((remainingMs % 3_600_000) / 60_000);
  if (hours > 0) return `${hours}h ${minutes}m until quarantine`;
  return `${minutes}m until quarantine`;
}

// ── ConflictCard ──────────────────────────────────────────────

interface ConflictCardProps {
  event: ChangeEvent;
  onResolved: () => void;
}

function ConflictCard({ event, onResolved }: ConflictCardProps) {
  const syncAdvisorEnabled = useAiCapability('sync_advisor');
  const [expanded, setExpanded] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState<ConflictAdvisorResult | null>(null);

  const conflict = event.conflictData as ConflictData | null;
  const isQuarantined = event.syncStatus === 'quarantined';
  const sourceLabel = SOURCE_LABEL[event.source] ?? event.source;

  async function getCurrentUserId(): Promise<string> {
    const supabase = getSafeSupabaseClient();
    const { data } = await supabase.auth.getUser();
    return data.user?.id ?? 'unknown';
  }

  async function handleResolve(winner: 'local' | 'external') {
    setIsResolving(true);
    setResolveError(null);
    const userId = await getCurrentUserId();
    const resolvedValue = winner === 'local' ? event.oldValue : event.newValue;
    const { error } = await resolveConflict(event.id, {
      winner,
      resolvedValue,
      resolvedBy: userId,
      note: note.trim() || undefined,
    });
    if (error) {
      setResolveError(error);
      setIsResolving(false);
    } else {
      onResolved();
    }
  }

  async function handleAiSuggest() {
    setIsAiLoading(true);
    setAiSuggestion(null);
    const result = await suggestConflictResolution({
      entityType: event.entityType,
      fieldName: event.fieldName,
      localValue: conflict?.localValue ?? event.oldValue,
      externalValue: conflict?.incomingValue ?? event.newValue,
      connectorType: SOURCE_LABEL[event.source] ?? event.source,
    });
    if (result) {
      setAiSuggestion(result);
      if (result.resolutionNote) setNote(result.resolutionNote);
    }
    setIsAiLoading(false);
  }

  async function handleDismiss() {
    setIsResolving(true);
    const supabase = getSafeSupabaseClient();
    await supabase
      .from('change_events')
      .update({ sync_status: 'skipped' })
      .eq('id', event.id);
    onResolved();
  }

  return (
    <div className={`border  overflow-hidden bg-[var(--bg-raised)] dark:bg-[var(--surface)] ${
      isQuarantined
        ? 'border-[var(--slate)] dark:border-[var(--slate)]'
        : 'border-[var(--coral)] dark:border-[var(--coral)]'
    }`}>
      {/* Header */}
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-start gap-3 px-5 py-4 hover:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)]/40 transition-colors text-left"
      >
        <span className="flex-shrink-0 mt-0.5 text-[var(--text-ter)]">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </span>

        {/* Status icon */}
        <span className={`flex-shrink-0 mt-0.5 ${isQuarantined ? 'text-[var(--text-ter)]' : 'text-[var(--coral)]'}`}>
          {isQuarantined ? <ShieldAlert className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
        </span>

        {/* Summary */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              {event.entityType} · {event.fieldName ?? 'entity'}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              isQuarantined
                ? 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 text-[var(--text-sec)]'
                : 'bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 text-[var(--coral)] dark:text-[var(--coral)]'
            }`}>
              {isQuarantined ? 'Quarantined' : 'Conflict'}
            </span>
          </div>
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">
            <span className="font-medium">{sourceLabel}</span> changed this field ·{' '}
            {formatTimestamp(event.createdAt)}
            {!isQuarantined && (
              <span className="ml-2 text-[var(--mustard)] dark:text-[var(--mustard)]">
                {timeUntilQuarantine(event.createdAt)}
              </span>
            )}
          </p>
        </div>
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-5 pb-5 border-t border-[var(--rule)] space-y-4">
          {/* Values comparison */}
          <div className="grid grid-cols-2 gap-3 pt-4">
            <div className="p-3 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 border border-[var(--slate)] dark:border-[var(--slate)]">
              <p className="text-xs font-semibold text-[var(--text-sec)] mb-1 uppercase tracking-wide">
                priorities.ai value
              </p>
              <p className="text-sm font-mono text-[var(--text-sec)] break-words">
                {formatValue(conflict?.localValue ?? event.oldValue)}
              </p>
              <p className="text-xs text-[var(--text-sec)] mt-1">Authoritative (local)</p>
            </div>
            <div className="p-3 bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20 border border-[var(--mustard)] dark:border-[var(--mustard)]">
              <p className="text-xs font-semibold text-[var(--mustard)] dark:text-[var(--mustard)] mb-1 uppercase tracking-wide">
                {sourceLabel} value
              </p>
              <p className="text-sm font-mono text-[var(--mustard)] dark:text-[var(--mustard)] break-words">
                {formatValue(conflict?.incomingValue ?? event.newValue)}
              </p>
              <p className="text-xs text-[var(--mustard)] dark:text-[var(--mustard)] mt-1">Incoming (external)</p>
            </div>
          </div>

          {/* Entity info */}
          <div className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] font-mono bg-[var(--surface)] dark:bg-[var(--bg-raised)]/50 px-3 py-2">
            Entity: {event.entityType} / {event.entityId}
          </div>

          {/* AI suggestion */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">Need guidance?</span>
            {syncAdvisorEnabled && (
            <button
              onClick={handleAiSuggest}
              disabled={isAiLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 text-[var(--text-sec)] hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/40 border border-[var(--slate)] dark:border-[var(--slate)] transition-colors disabled:opacity-50"
            >
              {isAiLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Bot className="h-3 w-3" />}
              Ask AI to suggest
            </button>
            )}
          </div>

          {aiSuggestion && (
            <div className="p-3 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 border border-[var(--slate)] dark:border-[var(--slate)] space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bot className="h-3.5 w-3.5 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
                  <span className="text-xs font-semibold text-[var(--text-sec)]">
                    AI recommends: {aiSuggestion.recommendedWinner === 'local' ? 'Keep priorities.ai' : `Accept ${sourceLabel}`}
                  </span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                    aiSuggestion.confidence === 'high'
                      ? 'bg-[var(--green-dim)] dark:bg-[var(--green)]/30 text-[var(--green)] dark:text-[var(--text-ter)]'
                      : aiSuggestion.confidence === 'medium'
                      ? 'bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/30 text-[var(--mustard)] dark:text-[var(--mustard)]'
                      : 'bg-[var(--hover-tint)] text-[var(--text-sec)]'
                  }`}>
                    {aiSuggestion.confidence} confidence
                  </span>
                </div>
                <button onClick={() => setAiSuggestion(null)} className="text-[var(--text-sec)] hover:text-[var(--text-pri)]">
                  <XCircle className="h-3.5 w-3.5" />
                </button>
              </div>
              <p className="text-xs text-[var(--text-sec)]">{aiSuggestion.rationale}</p>
            </div>
          )}

          {/* Resolution note */}
          <div>
            <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
              Resolution note (optional)
            </label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Why this value was chosen…"
              className="w-full px-3 py-2 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {resolveError && (
            <div className="flex items-center gap-2 text-sm text-[var(--coral)] dark:text-[var(--coral)] bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 px-3 py-2">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              {resolveError}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => handleResolve('local')}
              disabled={isResolving}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-50 text-white transition-colors"
            >
              {isResolving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              Keep priorities.ai value
            </button>
            <button
              onClick={() => handleResolve('external')}
              disabled={isResolving}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-[var(--mustard)] hover:bg-[var(--mustard)] disabled:opacity-50 text-white transition-colors"
            >
              {isResolving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowLeftRight className="h-3.5 w-3.5" />}
              Accept {sourceLabel} value
            </button>
            <button
              onClick={handleDismiss}
              disabled={isResolving}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-lift)] disabled:opacity-50 transition-colors"
            >
              <XCircle className="h-3.5 w-3.5" />
              Dismiss
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────

export default function ConflictQueue() {
  const [events, setEvents] = useState<ChangeEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'conflicted' | 'quarantined'>('all');
  const [isDispatching, setIsDispatching] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [dispatchResult, setDispatchResult] = useState<string | null>(null);
  const [isSweeping, setIsSweeping] = useState(false);

  const workspaceId = getCurrentWorkspaceId();

  const load = useCallback(async () => {
    if (!workspaceId) { setError('No workspace context'); setIsLoading(false); return; }
    setIsLoading(true);
    setError(null);
    try {
      const supabase = getSafeSupabaseClient();
      let query = supabase
        .from('change_events')
        .select('*')
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false })
        .limit(200);

      if (filter === 'conflicted') {
        query = query.eq('sync_status', 'conflicted');
      } else if (filter === 'quarantined') {
        query = query.eq('sync_status', 'quarantined');
      } else {
        query = query.in('sync_status', ['conflicted', 'quarantined']);
      }

      const { data, error: dbErr } = await query;
      if (dbErr) throw new Error(dbErr.message);

      setEvents((data ?? []).map((row) => ({
        id: row.id as string,
        workspaceId: row.workspace_id as string,
        source: row.source as string,
        entityType: row.entity_type as string,
        entityId: row.entity_id as string,
        fieldName: row.field_name as string | null,
        oldValue: row.old_value,
        newValue: row.new_value,
        eventType: row.event_type as ChangeEvent['eventType'],
        syncStatus: row.sync_status as SyncStatus,
        conflictData: row.conflict_data as ConflictData | null,
        createdAt: row.created_at as string,
      })));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load conflicts');
    } finally {
      setIsLoading(false);
    }
  }, [workspaceId, filter]);

  useEffect(() => { load(); }, [load]);

  // Auto-sweep quarantines on mount
  useEffect(() => {
    (async () => {
      setIsSweeping(true);
      const { quarantined } = await sweepQuarantines();
      if (quarantined > 0) load();
      setIsSweeping(false);
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleTriggerDispatch() {
    if (!workspaceId) return;
    setIsDispatching(true);
    setDispatchResult(null);
    const { data, error: dispErr } = await triggerDispatch({ workspaceId, mode: 'pending' });
    if (dispErr) {
      setDispatchResult(`Dispatch error: ${dispErr}`);
    } else if (data) {
      setDispatchResult(
        `Dispatch complete — ${data.dispatched} sent, ${data.conflicts} conflicts, ${data.failed} failed`
      );
    }
    setIsDispatching(false);
    load();
  }

  async function handleTriggerApply() {
    if (!workspaceId) return;
    setIsApplying(true);
    setDispatchResult(null);
    const result = await triggerApply(workspaceId, { mode: 'pending' });
    if (result.error) {
      setDispatchResult(`Apply error: ${result.error}`);
    } else {
      setDispatchResult(
        `Apply complete — ${result.applied} applied, ${result.conflicts} conflicts, ${result.no_mapping} unmapped`
      );
    }
    setIsApplying(false);
    load();
  }

  const conflicts = events.filter((e) => e.syncStatus === 'conflicted');
  const quarantined = events.filter((e) => e.syncStatus === 'quarantined');
  const shown = filter === 'conflicted' ? conflicts : filter === 'quarantined' ? quarantined : events;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<AlertTriangle className="h-4 w-4 text-[var(--coral)]" />}
        iconBg="bg-[var(--coral-dim)]"
        title="Conflict Queue"
        subtitle="Review and resolve change events where an external system attempted to modify a priorities.ai-owned field."
      />

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-4">
        <div className="p-4 border border-[var(--coral)] dark:border-[var(--coral)] bg-[var(--bg-raised)] dark:bg-[var(--surface)]">
          <div className="text-2xl font-semibold text-[var(--coral)] dark:text-[var(--coral)]">{conflicts.length}</div>
          <div className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">Active conflicts</div>
        </div>
        <div className="p-4 border border-[var(--slate)] dark:border-[var(--slate)] bg-[var(--bg-raised)] dark:bg-[var(--surface)]">
          <div className="text-2xl font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)]">{quarantined.length}</div>
          <div className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">Quarantined (past 48h SLA)</div>
        </div>
        <div className="p-4 border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)]">
          <div className="text-2xl font-semibold text-[var(--text-sec)] dark:text-[var(--text-pri)]">{events.length}</div>
          <div className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">Total needing review</div>
        </div>
      </div>

      {/* Actions bar */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Filter tabs */}
        <div className="flex items-center gap-1 p-1 bg-[var(--hover-tint)]">
          {([
            { value: 'all', label: 'All' },
            { value: 'conflicted', label: 'Conflicts' },
            { value: 'quarantined', label: 'Quarantined' },
          ] as const).map((f) => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`px-3 py-1.5 text-xs font-medium  transition-colors ${
                filter === f.value
                  ? 'bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] shadow-sm'
                  : 'text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-sec)]'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="flex-1" />

        {/* Refresh */}
        <button
          onClick={load}
          disabled={isLoading}
          className="flex items-center gap-1.5 px-3 py-2 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-lift)] disabled:opacity-50 transition-colors"
        >
          {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5" />}
          Refresh
        </button>

        {/* Trigger apply (inbound) */}
        <button
          onClick={handleTriggerApply}
          disabled={isApplying}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-50 text-white transition-colors"
          title="Apply pending inbound changes from external systems to local entities"
        >
          {isApplying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowDownToLine className="h-3.5 w-3.5" />}
          Run apply
        </button>

        {/* Trigger dispatch (outbound) */}
        <button
          onClick={handleTriggerDispatch}
          disabled={isDispatching}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-50 text-white transition-colors"
          title="Dispatch pending priorities.ai changes to connected external systems"
        >
          {isDispatching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
          Run dispatch
        </button>
      </div>

      {/* Dispatch result */}
      {dispatchResult && (
        <div className="flex items-center gap-2 px-4 py-3 bg-[var(--surface)] dark:bg-[var(--bg-raised)]/50 border border-[var(--rule)] text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
          <CheckCircle2 className="h-4 w-4 text-[var(--green)] flex-shrink-0" />
          {dispatchResult}
        </div>
      )}

      {/* Sweep indicator */}
      {isSweeping && (
        <div className="flex items-center gap-2 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          <Loader2 className="h-3 w-3 animate-spin" />
          Checking for quarantine SLA violations…
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 border border-[var(--coral)] dark:border-[var(--coral)] text-sm text-[var(--coral)] dark:text-[var(--coral)]">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Event list */}
      {!error && !isLoading && shown.length === 0 && (
        <div className="text-center py-16 text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          <CheckCircle2 className="h-8 w-8 mx-auto mb-3 opacity-40" />
          <p className="text-sm font-medium">No conflicts to review</p>
          <p className="text-xs mt-1">
            Conflicts appear here when an external system tries to change a priorities.ai-owned field.
          </p>
        </div>
      )}

      {isLoading && shown.length === 0 && (
        <div className="flex items-center justify-center py-16 text-[var(--text-ter)]">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          Loading…
        </div>
      )}

      {shown.length > 0 && (
        <div className="space-y-3">
          {/* Quarantine warning if any past SLA */}
          {quarantined.length > 0 && filter !== 'conflicted' && (
            <div className="flex items-start gap-3 px-4 py-3 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 border border-[var(--slate)] dark:border-[var(--slate)] text-sm text-[var(--text-sec)]">
              <ShieldAlert className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-medium">{quarantined.length} event{quarantined.length !== 1 ? 's' : ''} quarantined.</span>{' '}
                These entities are read-only across all systems until resolved.
                The 48-hour review SLA was exceeded.
              </div>
            </div>
          )}

          {shown.map((event) => (
            <ConflictCard
              key={event.id}
              event={event}
              onResolved={load}
            />
          ))}
        </div>
      )}

      {/* Phase info footer */}
      <div className="flex items-start gap-3 px-4 py-3 bg-[var(--surface)] dark:bg-[var(--bg-raised)]/50 border border-[var(--rule)] text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
        <Clock className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
        <div>
          <span className="font-medium">Phase 2 — Manual conflict resolution.</span>{' '}
          Accepting the external value currently marks it as resolved but does not
          automatically apply it to the local entity. Phase 3 will automate inbound
          application for fields sourced in the external system.
          The 48-hour quarantine SLA is configurable per workspace in Phase 3.
        </div>
      </div>
    </div>
  );
}
